import React, { useState } from 'react';
import moment from 'moment';
import Modal from 'src/shared/components/modal/Modal';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import ErrorMessage from 'src/shared/components/error-msg/ErrorMessage';
import { ControlledConfirmDialog } from 'src/shared/components/confirm/ControlledConfirmDialog';
import {
    useApproveEdnaDataReuploadMutation,
    useDismissEdnaDataReuploadMutation,
} from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import type { EdnaDataReuploadReviewItem } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import styles from './ProjectRefreshReviewModal.module.scss';

interface EdnaDataReuploadReviewModalProps {
    requestId: string;
    requestor: string;
    createdAt: string;
    status: string;
    errorDetails: string | null;
    reviewData: EdnaDataReuploadReviewItem[];
    onClose: () => void;
    onActionComplete: () => void;
}

const formatDate = (iso: string) => moment(iso).format('DD MMM YYYY HH:mm');
const formatRange = (from: string, to: string) => `${formatDate(from)} – ${formatDate(to)}`;

const REVIEW_FAILED = 'REVIEW_FAILED';

const EdnaDataReuploadReviewModal = ({
    requestId,
    requestor,
    createdAt,
    status,
    errorDetails,
    reviewData,
    onClose,
    onActionComplete,
}: EdnaDataReuploadReviewModalProps) => {
    const [mutationError, setMutationError] = useState<string | null>(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [approveEdnaDataReupload, { isLoading: isApproving }] = useApproveEdnaDataReuploadMutation();
    const [dismissEdnaDataReupload, { isLoading: isDismissing }] = useDismissEdnaDataReuploadMutation();

    const isActioning = isApproving || isDismissing;
    const isFailed = status === REVIEW_FAILED;

    const handleApprove = async () => {
        setMutationError(null);
        try {
            await approveEdnaDataReupload({ requestId }).unwrap();
            setIsConfirmOpen(false);
            onActionComplete();
            onClose();
        } catch {
            setIsConfirmOpen(false);
            setMutationError('Failed to approve the eDNA data reupload request.');
        }
    };

    const handleDismiss = async () => {
        setMutationError(null);
        try {
            await dismissEdnaDataReupload({ requestId }).unwrap();
            onActionComplete();
            onClose();
        } catch {
            setMutationError('Failed to dismiss the eDNA data reupload request.');
        }
    };

    return (
        <Modal onClose={onClose} dismissOnClickOutside={false}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2 className={styles.title}>{isFailed ? 'eDNA Data Reupload Review Failed' : 'Review eDNA Data Reupload Request'}</h2>
                    <div className={styles.meta}>
                        <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Requested by</span>
                            <span className={styles.metaValue}>{requestor}</span>
                        </div>
                        <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Submitted</span>
                            <span className={styles.metaValue}>{formatDate(createdAt)}</span>
                        </div>
                    </div>
                </div>

                {mutationError && <ErrorMessage message={mutationError} />}

                {isFailed && errorDetails && (
                    <div className={styles.errorSummary}>
                        <div className={styles.errorSummaryTitle}>Error details</div>
                        <pre className={styles.errorSummaryBody}>{errorDetails.trim()}</pre>
                    </div>
                )}

                {!isFailed && reviewData.length > 0 && (
                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>File</th>
                                    <th>Habitat</th>
                                    <th>Assay</th>
                                    <th>Samples count</th>
                                    <th>Sample collected date range</th>
                                    <th>Samples received date range</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviewData.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.file}</td>
                                        <td>{item.habitat}</td>
                                        <td>{item.assay}</td>
                                        <td>{item.samplesCount}</td>
                                        <td>{formatRange(item.sampleCollectedDateRange.from, item.sampleCollectedDateRange.to)}</td>
                                        <td>{formatRange(item.samplesReceivedDateRange.from, item.samplesReceivedDateRange.to)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className={styles.actions}>
                    <SecondaryButton onClick={handleDismiss} disabled={isActioning}>
                        {isDismissing ? 'Dismissing...' : isFailed ? 'OK' : 'Decline'}
                    </SecondaryButton>
                    {!isFailed && (
                        <PrimaryButton onClick={() => setIsConfirmOpen(true)} disabled={isActioning}>
                            Upload Data
                        </PrimaryButton>
                    )}
                </div>
            </div>
            <ControlledConfirmDialog
                open={isConfirmOpen}
                onOpenChange={setIsConfirmOpen}
                variant='confirm'
                title='Confirm eDNA Data Reupload'
                description='Are you sure you want to proceed? This will replace the existing eDNA data for this project with the new upload. Once approved, the request will be submitted to the processing workflow — you can track the final outcome in the Slack #result-live channel.'
                confirmLabel='Yes, proceed'
                isConfirming={isApproving}
                onConfirm={handleApprove}
            />
        </Modal>
    );
};

export default EdnaDataReuploadReviewModal;
